import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { SummaryList, Button, Select as NHSSelect, Input, Card, ErrorSummary, Checkboxes } from "nhsuk-react-components";
import Modal from '../modal/Modal.component';
import { debounce } from 'lodash';
import { listImplants, listImplantsByIds } from '../../actions/implantActions';
import { createPendingImplant } from '../../actions/pendingImplantActions';
import { PENDING_IMPLANT_CREATE_RESET } from '../../constants/pendingImplantConstants';

const ImplantEntitySelect = ({
  selectedId = null,
  onChange,
  nameProperty = 'barcode',
  idProperty = 'id',
  summaryKeys = ['barcode', 'manufacturer', 'modelName', 'modelNumber'],
  title = "Select Implant",
  enableCreate = true
}) => {
  const dispatch = useDispatch();
  const [filter, setFilter] = useState('');
  const [searchProp, setSearchProp] = useState('barcode');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newImplant, setNewImplant] = useState({
    modelName: '',
    barcode: '',
    manufacturer: '',
    modelNumber: '',
    modelCategory: '',
    MRConditional: false,
    status: 'Active'
  });

  const implantList = useSelector((state) => state.implantList) || {};
  const { loading = false, implants = [], error } = implantList;

  const implantListByIds = useSelector((state) => state.implantListByIds) || {};
  const { loading: loadingSelected = false, implants: selectedImplants = [], error: errorSelected } = implantListByIds;

  const pendingImplantCreate = useSelector((state) => state.pendingImplantCreate) || {};
  const { success: successPendingCreate, error: pendingCreateError, loading: creatingPendingImplant, pendingImplant: createdPendingImplant } = pendingImplantCreate;

  const userLogin = useSelector((state) => state.userLogin) || {};
  const { userInfo } = userLogin;
  const isSuperUser = userInfo?.roles?.includes('SuperUser');

  const isInitialMount = useRef(true);

  // Load selected implant by ID when selectedId changes
  useEffect(() => {
    if (selectedId) {
      dispatch(listImplantsByIds([selectedId]));
    } else {
      setSelectedEntity(null);
    }
  }, [dispatch, selectedId]);

  // Update selectedEntity when selectedImplants changes
  useEffect(() => {
    if (selectedImplants.length > 0 && selectedImplants[0].id === selectedId) {
      setSelectedEntity(selectedImplants[0]);
      if (isInitialMount.current) {
        isInitialMount.current = false;
        onChange?.(selectedImplants[0]);
      }
    }
  }, [selectedImplants, selectedId, onChange]);

  // Handle successful pending implant creation
  useEffect(() => {
    if (successPendingCreate && createdPendingImplant) {
      setCreateModalOpen(false);
      setNewImplant({
        modelName: '',
        barcode: '',
        manufacturer: '',
        modelNumber: '',
        modelCategory: '',
        MRConditional: false,
        status: 'Active'
      });
      // Show success message - implant is pending approval
      if (isSuperUser) {
        alert('Pending implant created! You can review and approve it in the Pending Implants dashboard.');
      } else {
        alert('Implant submitted for approval! A SuperUser will review it shortly.');
      }
      dispatch({ type: PENDING_IMPLANT_CREATE_RESET });
    }
  }, [successPendingCreate, createdPendingImplant, dispatch, isSuperUser]);

  // Load implants based on filter for search
  useEffect(() => {
    if (filter.length >= 2 || filter.length === 0) {
      dispatch(listImplants({ filter: searchProp, query: filter }));
    }
  }, [dispatch, filter, searchProp]);

  const handleChange = (selectedOption) => {
    if (selectedOption && selectedOption.value === 'create_new') {
      setCreateModalOpen(true);
    } else if (selectedOption) {
      const selectedImplant = implants.find(i => i[idProperty] === selectedOption.value);
      if (selectedImplant) {
        setSelectedEntity(selectedImplant);
        onChange?.(selectedImplant);
      }
    } else {
      setSelectedEntity(null);
      onChange?.(null);
    }
    setFilter(''); // Reset the filter
  };

  const handleSearchChange = debounce((value) => {
    if (value.length >= 2 || value.length === 0) {
      setFilter(value);
    }
  }, 300);

  const handleEntityClick = (entity, event) => {
    event.preventDefault();
    setSelectedEntity(entity);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleCreateImplant = () => {
    setCreateModalOpen(true);
  };

  const handleCreateSubmit = () => {
    // Validate required fields
    if (!newImplant.manufacturer || !newImplant.modelName || !newImplant.modelCategory) {
      alert('Please fill in all required fields: Manufacturer, Model Name, and Model Category');
      return;
    }

    console.log('Creating pending implant:', newImplant);
    console.log('Is SuperUser:', isSuperUser);
    console.log('Dispatching createPendingImplant action');
    
    // Everyone creates pending implants (SuperUser can approve them later)
    dispatch(createPendingImplant(newImplant));
  };

  const searchProperties = [
    { value: 'barcode', label: 'Barcode' },
    { value: 'manufacturer', label: 'Manufacturer' },
    { value: 'modelName', label: 'Model Name' },
    { value: 'modelNumber', label: 'Model Number' }
  ];

  const createNewOption = {
    value: 'create_new',
    label: '+ Add New Implant',
  };

  const optionLabel = (implant) =>
    [implant.barcode, implant.manufacturer, implant.modelName, implant.modelNumber]
      .filter(Boolean)
      .join(' | ') || '—';

  const options = [
    ...(enableCreate ? [createNewOption] : []),
    ...implants.map(implant => ({
      value: implant[idProperty],
      label: optionLabel(implant)
    }))
  ];

  const selectedValue = selectedEntity ? {
    value: selectedEntity[idProperty],
    label: optionLabel(selectedEntity)
  } : null;

  return (
    <React.Fragment>
      <div style={{ marginBottom: '1rem' }}>
        <NHSSelect
          label="Search By"
          value={searchProp}
          onChange={(e) => setSearchProp(e.target.value)}
        >
          {searchProperties.map(prop => (
            <NHSSelect.Option key={prop.value} value={prop.value}>
              {prop.label}
            </NHSSelect.Option>
          ))}
        </NHSSelect>
      </div>

      <Select
        value={selectedValue}
        onChange={handleChange}
        onInputChange={handleSearchChange}
        options={options}
        isLoading={loading || loadingSelected}
        isClearable
        placeholder="Search implants or add new..."
        noOptionsMessage={() => filter.length < 2 ? "Type at least 2 characters to search" : "No implants found"}
        filterOption={(option, input) => {
          if (option.value === 'create_new') return true;
          if (!input) return true;
          return option.label.toLowerCase().includes(input.toLowerCase());
        }}
      />

      {selectedEntity && (
        <p style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
          Device selected:{' '}
          <a
            href="#"
            onClick={(event) => {
              event.preventDefault();
              handleEntityClick(selectedEntity, event);
            }}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
          >
            {selectedEntity.barcode || selectedEntity.modelName || selectedEntity[nameProperty] || 'View details'}
          </a>
        </p>
      )}

      {error && <p style={{ color: 'red', marginTop: '0.5rem' }}>{error}</p>}
      {errorSelected && <p style={{ color: 'red', marginTop: '0.5rem' }}>{errorSelected}</p>}

      <Modal show={modalOpen} handleClose={closeModal}>
        {selectedEntity && (
          <>
            <h2>{selectedEntity[nameProperty] || selectedEntity.barcode || 'Implant Details'}</h2>
            <SummaryList>
              {summaryKeys.map(key => (
                <SummaryList.Row key={key}>
                  <SummaryList.Key>
                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim()}
                  </SummaryList.Key>
                  <SummaryList.Value>
                    {selectedEntity[key] ? selectedEntity[key].toString() : "-"}
                  </SummaryList.Value>
                </SummaryList.Row>
              ))}
            </SummaryList>
            <Button onClick={closeModal}>Close</Button>
          </>
        )}
      </Modal>

      {enableCreate && (
        <Modal show={createModalOpen} handleClose={() => {
          setCreateModalOpen(false);
          dispatch({ type: PENDING_IMPLANT_CREATE_RESET });
        }}>
          <Card>
            <Card.Content>
              <Card.Heading className="nhsuk-heading-m">
                Submit Implant for Approval
              </Card.Heading>
              <Card.Description>
                <p style={{ marginBottom: '1rem', color: '#003087' }}>
                  {isSuperUser 
                    ? 'Your submission will be added to pending implants. You can review and approve it in the Pending Implants dashboard.'
                    : 'Your implant submission will be reviewed by a SuperUser before being added to the system.'}
                </p>
                {pendingCreateError && (
                  <ErrorSummary>
                    <ErrorSummary.Title id="error-summary-title">
                      There is a problem
                    </ErrorSummary.Title>
                    <ErrorSummary.Body>
                      <ul>
                        {Array.isArray(pendingCreateError) ? pendingCreateError.map((err, index) => (
                          <li key={index}>{err}</li>
                        )) : <li>{pendingCreateError}</li>}
                      </ul>
                    </ErrorSummary.Body>
                  </ErrorSummary>
                )}

                <Input
                  id="barcode"
                  label="Barcode/GTIN"
                  name="barcode"
                  type="text"
                  onChange={(e) => setNewImplant({ ...newImplant, barcode: e.target.value })}
                  value={newImplant.barcode}
                  required
                />

                <Input
                  id="manufacturer"
                  label="Manufacturer"
                  name="manufacturer"
                  type="text"
                  onChange={(e) => setNewImplant({ ...newImplant, manufacturer: e.target.value })}
                  value={newImplant.manufacturer}
                  required
                />

                <Input
                  id="modelName"
                  label="Model Name"
                  name="modelName"
                  type="text"
                  onChange={(e) => setNewImplant({ ...newImplant, modelName: e.target.value })}
                  value={newImplant.modelName}
                  required
                />

                <Input
                  id="modelNumber"
                  label="Model Number"
                  name="modelNumber"
                  type="text"
                  onChange={(e) => setNewImplant({ ...newImplant, modelNumber: e.target.value })}
                  value={newImplant.modelNumber}
                />

                <NHSSelect
                  id="modelCategory"
                  label="Model Category"
                  name="modelCategory"
                  onChange={(e) => setNewImplant({ ...newImplant, modelCategory: e.target.value })}
                  value={newImplant.modelCategory}
                  required
                >
                  <NHSSelect.Option value="">Select a model category</NHSSelect.Option>
                  <NHSSelect.Option value="Accessory">Accessory</NHSSelect.Option>
                  <NHSSelect.Option value="Generator">Generator</NHSSelect.Option>
                  <NHSSelect.Option value="Lead">Lead</NHSSelect.Option>
                  <NHSSelect.Option value="Implantable Monitor">Implantable Monitor</NHSSelect.Option>
                </NHSSelect>

                <Checkboxes name="checkboxes">
                  <Checkboxes.Box
                    checked={newImplant.MRConditional || false}
                    onChange={(e) => setNewImplant({ ...newImplant, MRConditional: e.target.checked })}
                  >
                    MRI Conditional
                  </Checkboxes.Box>
                </Checkboxes>
              </Card.Description>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <Button
                  onClick={handleCreateSubmit}
                  disabled={creatingPendingImplant}
                >
                  {creatingPendingImplant ? 'Submitting...' : 'Submit for Approval'}
                </Button>
                <Button
                  secondary
                  onClick={() => {
                    setCreateModalOpen(false);
                    dispatch({ type: PENDING_IMPLANT_CREATE_RESET });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Card.Content>
          </Card>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default ImplantEntitySelect;

